import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, ShieldCheck, Wrench, Receipt, Truck } from "lucide-react";
import { scrollToId } from "../../lib/smooth";
import { PHONE, TEL } from "../../lib/data";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [
  { text: "We bring", cls: "text-white" },
  { text: "the shop", cls: "text-chrome" },
  { text: "to you.", cls: "text-neon pr-4" },
];
const PERKS = [
  { icon: Truck, label: "100% mobile" },
  { icon: ShieldCheck, label: "Insured" },
  { icon: Wrench, label: "$85/hr labor" },
  { icon: Receipt, label: "Written quote first" },
];

const RotatingBadge = () => (
  <div className="pointer-events-none absolute -left-6 -top-8 z-20 h-28 w-28 sm:-left-10 sm:h-36 sm:w-36">
    <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
      <defs>
        <path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
      </defs>
      <circle cx="100" cy="100" r="98" fill="#07070b" stroke="url(#g)" strokeWidth="2" />
      <linearGradient id="g" x1="0" x2="1"><stop offset="0" stopColor="#ff2bd6" /><stop offset="1" stopColor="#8b2cff" /></linearGradient>
      <text fill="#fff" fontSize="17" fontWeight="800" letterSpacing="4" fontFamily="Saira">
        <textPath href="#circ" textLength="480">CALL OR TEXT • 248-820-9599 • </textPath>
      </text>
    </svg>
    <Wrench className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 -rotate-45 text-[var(--mag)]" />
  </div>
);

const TiltFrame = ({ y }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ perspective: 1200 }}
      className="relative mx-auto w-full max-w-[460px]"
      data-testid="hero-photo-frame"
    >
      <RotatingBadge />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_50%_40%,rgba(255,43,214,0.45),transparent_65%)] blur-2xl" />
        <div className="clip-race relative aspect-[4/5] overflow-hidden bg-[#111]">
          <motion.img style={{ y }} src="/img/mech3.webp" alt="Mechanic working under the hood" className="absolute inset-0 h-[115%] w-full object-cover object-[50%_30%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-transparent to-transparent" />
          <div className="absolute inset-0 mix-blend-color bg-[linear-gradient(160deg,rgba(139,44,255,0.35),rgba(255,43,214,0.15))]" />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -bottom-6 left-4 right-4 rounded-2xl border border-white/10 bg-[#0b0b12]/85 p-4 backdrop-blur-xl sm:left-8 sm:right-auto sm:w-72">
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">Labor rate</div>
          <div className="mt-1 flex items-end gap-2">
            <span className="font-race text-5xl text-neon">$85</span>
            <span className="pb-2 text-sm text-zinc-300">/ hour · parts extra</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain relative overflow-hidden pb-24 pt-32 sm:pt-36 lg:min-h-[100svh] lg:pb-16">
      <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_30%_30%,black,transparent_75%)]" />
      <div className="absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[var(--vio)]/25 blur-[140px]" />
      <div className="absolute -right-20 bottom-0 h-[420px] w-[420px] rounded-full bg-[var(--mag)]/20 blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-zinc-300">
            <span className="relative flex h-2 w-2"><span className="absolute h-full w-full animate-ping rounded-full bg-[var(--mag)] opacity-75" /><span className="h-2 w-2 rounded-full bg-[var(--mag)]" /></span>
            Bridgeway Auto · Mobile Mechanic
          </motion.div>
          <h1 data-testid="hero-headline" className="font-race text-[19vw] leading-[0.86] sm:text-[8rem] lg:text-[9.5rem]">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-[0.06em]">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "105%", skewY: 6 }}
                  animate={{ y: "0%", skewY: 0 }}
                  transition={{ duration: 1.05, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }} className="mt-8 max-w-xl text-base text-zinc-300 md:text-lg">
            Diagnostics, brakes, oil changes, repairs and full swaps — done right in your driveway. Book online and we'll call you to confirm the time.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <motion.button whileTap={{ scale: 0.97 }} data-testid="hero-book-button" onClick={() => scrollToId("book")} className="btn-neon group flex items-center justify-center gap-3 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white">
              Book an appointment
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </motion.button>
            <a href={TEL} data-testid="hero-call-button" className="flex items-center justify-center gap-3 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:border-[var(--mag)] hover:bg-white/5">
              <Phone className="h-4 w-4 text-[var(--mag)]" /> {PHONE}
            </a>
          </motion.div>
          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:gap-8">
            {PERKS.map((p, i) => (
              <motion.div key={p.label} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 + i * 0.08 }} className="flex items-center gap-2 text-sm font-semibold text-zinc-300">
                <p.icon className="h-4 w-4 text-[var(--mag)]" /> {p.label}
              </motion.div>
            ))}
          </div>
        </motion.div>
        <div className="lg:col-span-5">
          <TiltFrame y={imgY} />
        </div>
      </div>
    </section>
  );
};
